/**
 * The questions asked before the Support at home plans run — inline the first time, and later under
 * "Things that change the answer" (Support at home rebuild, 19 Oct 2026; the agreed mock-up's
 * questions screen).
 *
 * Owner rulings built in:
 *  - "What's this?" on every question, so a family sees why it is asked (comment of 18 Oct).
 *  - The levers are listed by the means test they work on: the test that sets THIS household's Age
 *    Pension decides which come first. All stay ticked — nothing is hidden, and the engine still tests
 *    every way (decision 8, 19 Oct). "Ticking is not a decision."
 *  - Whether someone is moving into aged care is asked on step 1, not here.
 *  - "Build a plan yourself" is offered on this screen as well as under the plans.
 *  - 10 years, as every module but aged care (owner, 19 Oct 2026).
 */
import React, { useState } from 'react';
import { formatCurrency, parseCurrency } from '../../utils/index.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { FUNERAL_BOND_LIMIT, GIFTING_LIMIT_ANNUAL, GIFTING_LIMIT_5_YEAR } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import GuaranteedIncomeQuestions, { type GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { QuestionTitle } from '../../components/strategy/QuestionParts.tsx';

/*
 * THE GUARANTEED INCOME IS NO LONGER A LEVER HERE (owner, 21 Oct 2026 — the guaranteed income review, mock-up
 * screen 6: "Support at home: the same three answers; a new share question"). The tick "Locking some money away
 * for a guaranteed income for life" is replaced by the one question every module asks
 * (GuaranteedIncomeQuestions): the explanation always on screen, three answers, and how much at most. The
 * answer is the CLIENT's (goals.annuityAppetite / annuityResidual / annuityShareCap*), read by every module,
 * so the engine's annuity switch follows it (annuityPreference.ts) rather than a tick on this screen.
 */
export type SahLeverId = 'home' | 'funeral' | 'gift' | 'debt' | 'property';
export const SAH_LEVER_IDS: readonly SahLeverId[] = ['home', 'funeral', 'gift', 'debt', 'property'];

export interface SahAnswers {
    years: number;
    emergencyReserve: number;
    levers: Record<SahLeverId, boolean>;
    /** '' = not sure: assume the most cautious (Conservative), recording nothing. */
    investProfile: string;
}

export type BindingTest = 'assets' | 'income' | null;

const WhatsThis = ({ children }: { children: React.ReactNode }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            {' '}
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="text-[12px] font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">
                what&apos;s this?
            </button>
            {open && <span className="mt-1.5 block rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13px] font-normal leading-relaxed text-slate-700">{children}</span>}
        </>
    );
};

/**
 * EVERY "WHAT'S THIS?" SAYS WHAT THE THING IS FIRST (owner, 20 Oct 2026: "You dont actually say what a
 * funeral bond is"; the investment note "reads poorly"). Then why it matters to them, then the catch —
 * each its own short line, never one dense paragraph.
 */
const Why = ({ lines }: { lines: React.ReactNode[] }) => (
    <span className="block space-y-1.5">
        {lines.map((l, i) => <span key={i} className="block">{l}</span>)}
    </span>
);
const b = (t: string) => <strong className="text-slate-900">{t}</strong>;

/** Numbered, as every module's questions are (owner, 23 Oct 2026). */
const Question = ({ n, label, why, children, testId }: { n: number; label: string; why: React.ReactNode; children: React.ReactNode; testId?: string }) => (
    <div data-testid={testId} className="border-b border-slate-100 py-4 last:border-b-0">
        <QuestionTitle n={n} as="p">{label}<WhatsThis>{why}</WhatsThis></QuestionTitle>
        <div className="mt-2">{children}</div>
    </div>
);

const Hint = ({ children }: { children: React.ReactNode }) => <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">{children}</p>;

interface LeverDef { id: SahLeverId; label: string; note: string; why: React.ReactNode; helpsUnder: Array<'assets' | 'income'> }

export interface SahQuestionsProps {
    value: SahAnswers;
    onChange: (next: SahAnswers) => void;
    factFind: DraftFactFind;
    bindingTest: BindingTest;
    /** The reserve the plans keep by default (the greater of six months' living costs and $50,000). */
    suggestedReserve: number;
    /** The client has debts on file (the debt lever is shown only then). */
    debtTotal: number;
    idPrefix?: string;
    /**
     * The guaranteed-income question (GuaranteedIncomeQuestions): the writer for the client's goals, and the
     * money a guaranteed income could come from, as the plans size it (sahAnnuityInvestable). Absent: not asked.
     */
    guaranteedIncome?: {
        onGoals: (patch: GuaranteedIncomeGoals) => void;
        investable: number;
        madeOf: string;
    };
}

export const SahQuestions = ({ value, onChange, factFind, bindingTest, suggestedReserve, debtTotal, idPrefix = 'sahq', guaranteedIncome }: SahQuestionsProps) => {
    const set = (patch: Partial<SahAnswers>) => onChange({ ...value, ...patch });
    const v = householdVoice(factFind);
    const hasHome = (Number(factFind.home?.value) || 0) > 0;
    const testWords = bindingTest === 'income' ? 'the income test' : 'the assets test';
    const levers: LeverDef[] = [
        ...(hasHome ? [{ id: 'home' as const, label: 'Spending on the home — renovating or upgrading it', note: 'The home is not counted.', why: <Why lines={[
            <>{b('What it is.')} Spending money on the home {v.they} live in: a renovation, or moving to a dearer home.</>,
            <>{b('Why it can help.')} Centrelink does not count the home {v.they} live in, for the Age Pension or for Support at home. Money moved into it stops being counted, which can lift the pension and lower what {v.they} pay for each hour of support.</>,
            <>{b('The catch.')} {v.They} only get the money back by selling the home.</>,
        ]} />, helpsUnder: ['assets', 'income'] as Array<'assets' | 'income'> }] : []),
        { id: 'funeral', label: `Prepaying ${v.their} funerals with a funeral bond`, note: 'Not counted, up to a limit.', why: <Why lines={[
            <>{b('What it is.')} An investment that can only be used to pay for a funeral. {v.They} put money in now, it is invested, and it is paid out when {v.they} die to cover the funeral.</>,
            <>{b('Why it can help.')} Up to {formatCurrency(FUNERAL_BOND_LIMIT)} each is not counted by Centrelink, for the Age Pension or for Support at home. That can lift the pension and lower what {v.they} pay for support.</>,
            <>{b('The catch.')} The money cannot be taken out for anything else.</>,
        ]} />, helpsUnder: ['assets', 'income'] },
        { id: 'gift', label: 'Giving money to family within the yearly free limits', note: 'Gifts above the limits still count for five years.', why: <Why lines={[
            <>{b('What it is.')} Giving money to family now, rather than leaving it in the estate.</>,
            <>{b('Why it can help.')} Centrelink stops counting up to {formatCurrency(GIFTING_LIMIT_ANNUAL)} given away in a year, and up to {formatCurrency(GIFTING_LIMIT_5_YEAR)} over five years. The limits are the same for a couple as for one person.</>,
            <>{b('The catch.')} Anything given above those limits is still counted as {v.you ? 'yours' : 'theirs'} for five years. A gift cannot be taken back.</>,
        ]} />, helpsUnder: ['assets', 'income'] },
        ...(debtTotal > 0 ? [{ id: 'debt' as const, label: `Paying off the ${formatCurrency(debtTotal)} of debt`, note: 'Less cash counted, and no more interest.', why: <Why lines={[
            <>{b('What it is.')} Using savings to pay off the {formatCurrency(debtTotal)} {v.they} owe.</>,
            <>{b('Why it can help.')} The money used is no longer counted by Centrelink, and the repayments and interest stop.</>,
            <>{b('The catch.')} It leaves less cash on hand.</>,
        ]} />, helpsUnder: ['assets', 'income'] as Array<'assets' | 'income'> }] : []),
        ...(hasHome ? [{ id: 'property' as const, label: 'Selling, renting out or borrowing against the home', note: `Selling or letting the home makes more of ${v.their} money counted.`, why: <Why lines={[
            <>{b('What it is.')} Turning part of the home into money: selling it and buying a cheaper one, a reverse mortgage (a loan against the home, repaid when it is sold), or the government's Home Equity Access Scheme (regular payments, also a loan against the home).</>,
            <>{b('Why it is less likely to help.')} The home is not counted by Centrelink, but money is. Moving value out of the home usually raises what is counted.</>,
            <>{b('Why we test it anyway.')} The money can still pay for the support, and for some families that matters more.</>,
        ]} />, helpsUnder: [] as Array<'assets' | 'income'> }] : []),
    ];
    const test = bindingTest ?? 'assets';
    const likely = levers.filter((l) => l.helpsUnder.includes(test));
    const lessLikely = levers.filter((l) => !l.helpsUnder.includes(test));
    const Lever = ({ l }: { l: LeverDef }) => (
        <label className="flex cursor-pointer items-start gap-2.5 py-1.5 text-[14px] text-slate-800">
            <input
                type="checkbox"
                checked={value.levers[l.id] !== false}
                onChange={(e) => set({ levers: { ...value.levers, [l.id]: e.target.checked } })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-blue-600"
                data-testid={`${idPrefix}-lever-${l.id}`}
            />
            <span>{l.label}<WhatsThis>{l.why}</WhatsThis> <span className="text-[13px] text-slate-500">{l.note}</span></span>
        </label>
    );

    // Numbered in the order shown; the screen's "Five questions…" heading counts the same.
    let asked = 0;
    const next = () => { asked += 1; return asked; };
    return (
        <div data-testid="sah-questions">
            <Question
                n={next()}
                label="How many years should we plan for?"
                testId={`${idPrefix}-q-years`}
                why={<Why lines={[
                    <>The plans are compared on what each leaves the family at the end of this period, so the period can change which plan comes out best.</>,
                    <>Some ways pay back slowly, such as spending on the home. Others help from the first year, such as an income for life.</>,
                ]} />}
            >
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        id={`${idPrefix}-years`}
                        type="number"
                        min={1}
                        max={30}
                        value={value.years}
                        onChange={(e) => set({ years: Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)) })}
                        className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-[15px] font-semibold text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor={`${idPrefix}-years`} className="text-[13px] text-slate-600">years</label>
                </div>
                <Hint>We plan for 10 years at home. Try a shorter or longer period to see how much the answer moves.</Hint>
            </Question>

            <Question
                n={next()}
                label="How much should stay in the bank for emergencies?"
                testId={`${idPrefix}-q-reserve`}
                why={<Why lines={[
                    <>{b('What it is.')} Money kept in the bank for the unexpected. We keep it back when we move {v.their} money, and draw on it only if a year runs short.</>,
                    <>{b('Why it matters.')} Centrelink still counts it. The bigger the reserve, the less money is left for the ways below to work with.</>,
                ]} />}
            >
                <input
                    id={`${idPrefix}-reserve`}
                    type="text"
                    inputMode="numeric"
                    value={value.emergencyReserve > 0 ? formatCurrency(value.emergencyReserve) : ''}
                    onChange={(e) => set({ emergencyReserve: Math.max(0, parseFloat(String(parseCurrency(e.target.value))) || 0) })}
                    placeholder="$0"
                    className="w-40 rounded-lg border border-slate-300 px-3 py-2 text-[15px] font-semibold tabular-nums text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
                <Hint>
                    We suggest {formatCurrency(suggestedReserve)}: the greater of six months&apos; living costs and $50,000
                    {Math.round(value.emergencyReserve) !== Math.round(suggestedReserve) && (
                        <> — <button type="button" onClick={() => set({ emergencyReserve: suggestedReserve })} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">use {formatCurrency(suggestedReserve)}</button></>
                    )}.
                </Hint>
            </Question>

            <Question
                n={next()}
                label={`Which of these would ${v.names} consider?`}
                testId={`${idPrefix}-q-levers`}
                why={<Why lines={[
                    <>Each of these moves money into something Centrelink counts less of, or not at all.</>,
                    <>Less counted can lift {v.their} Age Pension and lower what {v.they} pay for each hour of support.</>,
                    <>Ticking one only lets us test it. It is not a decision to do it.</>,
                ]} />}
            >
                <p className="mb-1 text-[13px] leading-relaxed text-slate-600">
                    {bindingTest
                        ? <>{v.Possessive} Age Pension is set by {testWords}, so the ways that lower {bindingTest === 'income' ? 'the income Centrelink counts' : 'what Centrelink counts'} are listed first.</>
                        : <>The ways that lower what Centrelink counts are listed first.</>}
                </p>
                {likely.length > 0 && (
                    <>
                        <p className="mb-0.5 mt-3 text-[13px] font-semibold text-brand-navy">Most likely to help — they lower {bindingTest === 'income' ? 'the income counted' : 'what is counted'}</p>
                        {likely.map((l) => <Lever key={l.id} l={l} />)}
                    </>
                )}
                {lessLikely.length > 0 && (
                    <>
                        <p className="mb-0.5 mt-3 text-[13px] font-semibold text-brand-navy">Less likely to help {v.them} — tested anyway</p>
                        {lessLikely.map((l) => <Lever key={l.id} l={l} />)}
                    </>
                )}
                <Hint>Ticking one is not a decision to do it — it only lets us test it. Nothing is hidden: every way is still tested.</Hint>
            </Question>

            {guaranteedIncome && (
                <div data-testid={`${idPrefix}-q-guaranteed`} className="border-b border-slate-100 py-4 last:border-b-0">
                    {/* People receiving support at home may buy the full-amount-back type (carePlusSplit.guaranteedShapeAvailable). */}
                    <GuaranteedIncomeQuestions
                        number={next()}
                        factFind={factFind}
                        onGoals={guaranteedIncome.onGoals}
                        investable={guaranteedIncome.investable}
                        investableLead="After the money kept for emergencies"
                        investableMadeOf={guaranteedIncome.madeOf}
                        inCare
                        idPrefix={idPrefix}
                    />
                </div>
            )}

            <Question
                n={next()}
                label="How would money that is freed up be invested?"
                testId={`${idPrefix}-q-invest`}
                why={<Why lines={[
                    <>{b('Why we ask.')} Some plans free up money, for example selling the home and buying a cheaper one. We have to assume how that money would be invested, because that sets what it earns each year in the figures.</>,
                    <>{b('The choices.')} A cautious choice assumes lower, steadier returns. A growth choice assumes higher returns that rise and fall more.</>,
                    <>{b(`If ${v.they} are not sure,`)} we assume the most cautious, so no plan looks better than it should. The Assumptions page shows what each choice is assumed to earn.</>,
                ]} />}
            >
                <select
                    id={`${idPrefix}-new-money-profile`}
                    aria-label="How freed-up money would be invested"
                    value={value.investProfile}
                    onChange={(e) => set({ investProfile: e.target.value })}
                    className="w-full max-w-md rounded-lg border border-slate-300 bg-white px-3 py-2 text-[14px] text-slate-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">Not sure — assume the most cautious</option>
                    {INVESTMENT_PROFILE_IDS.map((id) => (
                        <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>
                    ))}
                </select>
            </Question>
        </div>
    );
};

/** The engine's lever switches from the answers. */
export function strategySettingsFromAnswers(a: SahAnswers) {
    const on = (id: SahLeverId) => a.levers[id] !== false;
    return {
        includeGifting: on('gift'),
        includeFuneralBonds: on('funeral'),
        // The client's answer to the guaranteed-income question decides (annuityPreference.ts), not a tick here.
        includeAnnuities: true,
        includeDebtRepayment: on('debt'),
        includePropertyStrategies: on('home') || on('property'),
        emergencyReserve: a.emergencyReserve,
        propertyStrategies: {
            increaseHomeEquity: on('home'),
            downsize: on('property'),
            downsizeNewHomeValue: 0,
            downsizeSellingCosts: 0,
            reverseMortgage: on('property'),
            heas: on('property'),
        },
    };
}

/** Saved answers from the profile, with the suggested defaults where nothing is recorded. */
export function answersFromGoals(goals: Record<string, unknown> | null | undefined, suggestedReserve: number): SahAnswers {
    const g = goals || {};
    const off = String(g.sahLeversOff || '').split(',').map((s) => s.trim()).filter(Boolean);
    const levers = Object.fromEntries(SAH_LEVER_IDS.map((id) => [id, !off.includes(id)])) as Record<SahLeverId, boolean>;
    const years = Number(g.sahLegacyYears);
    const reserve = Number(g.sahEmergencyReserve);
    return {
        years: Number.isFinite(years) && years >= 1 ? years : 10,
        emergencyReserve: Number.isFinite(reserve) && reserve > 0 ? reserve : suggestedReserve,
        levers,
        investProfile: String(g.newMoneyInvestmentProfile || ''),
    };
}

/** The goals keys the answers are saved under (all in factFindSchema.ts). */
export function goalsFromAnswers(a: SahAnswers): Record<string, unknown> {
    const off = SAH_LEVER_IDS.filter((id) => a.levers[id] === false).join(',');
    return {
        sahSetupSeen: true,
        sahLegacyYears: a.years,
        sahEmergencyReserve: Math.round(a.emergencyReserve),
        sahLeversOff: off,
        ...(a.investProfile ? { newMoneyInvestmentProfile: a.investProfile } : {}),
    };
}
